import { DEACTIVE_PANELS_EDITOR } from 'actions';
import { dispatch, state } from '__common/store';
import { confirmPendingPanelRemovalAndContinue, savePanelsEditor } from '__editor/panelsEditor/components/panels/panels';
import { SET_USER_CLICKED_YES_OR_NO, TERMINATE_ADVANCE_ROOF_SELECTING, TURN_OFF_MEASUREMENT_TOOL } from '__editor/googleMapsRoofsSelector/googleMapsRoofsSelectorActions';
import { setShouldSkipFetZipCode } from '__editor/components/roofsSelector/components/roofsSelectorMap/roofsSelectorMap';
import { hasRoofObstructionHeightSet } from './components/obstructions/obstructions';
import { launchRecheckPanelExposure } from './components/panels/utils/exposureRecheck';
import { isBlankMap } from '__common/constants/map';
import { isRMFamily, isRMIFIProduct, isEcoFoot2Plus, isRmGridflex10D, isRmGridflex5Rwdi } from '__common/constants/products';
import { isASCE716or722Selected } from 'projectDesign/components/projectConfiguration/projectConfiguration'
import { applyEcoFoot2PlusRM10andEvoSetbackChanges } from '__common/utils/versionCompare/versionCompare';

let editorDims = null;

export function panelsEditorEnabled() {
  const { background: { cords }, moduleSelector: { modelData } } = state();
  const panelsEditorEnabled = cords;
  return panelsEditorEnabled && Object.keys(modelData).length;
}

export function deactivePanelsEditor(cb?: Function) {
  const { background: { selectedRoofId }, projectConfiguration } = state();
  
  if(projectConfiguration?.projectEnvConfig?.helioscope_id){
    dispatch(SET_USER_CLICKED_YES_OR_NO(selectedRoofId, true));
  }

  const deactivateAction = () => {
    savePanelsEditor();
    dispatch(DEACTIVE_PANELS_EDITOR());
    dispatch(TERMINATE_ADVANCE_ROOF_SELECTING());
    dispatch(TURN_OFF_MEASUREMENT_TOOL());
    setShouldSkipFetZipCode(true);

    if (cb) {
      cb();
    }
  };

  return confirmPendingPanelRemovalAndContinue(() => {
    if (hasRoofObstructionHeightSet()) {
      const panelsIdsToRecheck = state().panels.panels.map(panel => panel.id);
      launchRecheckPanelExposure(panelsIdsToRecheck, selectedRoofId, () => {
        deactivateAction();
      });
    } else {
      deactivateAction();
    }
  });
}

export function setEditorDims(width, height) {
  editorDims = {
    width,
    height,
  };
}

export function getEditorDims() {
  return editorDims;
}

// stateConfig is threaded in from callers that run synchronously inside a
// reducer (the panel-exposure recheck chain). Calling store.getState() there
// throws "You may not call store.getState() while the reducer is executing",
// which aborted the exposure recheck and, in turn, the autosave callback that
// depends on it (leaving Lite stuck on "Not saved yet" with an empty BOM).
// Callers outside the reducer path omit it and fall back to state() as before.
const isBlankMapInLite = (mapType: string, stateConfig?: any): boolean => {
  const s = stateConfig || state();
  const isLite = s?.app?.isUbuilderLite;
  const currentMapType = mapType || s?.roofsSelector?.mapType;
  return Boolean(isLite && isBlankMap(currentMapType));
};

export function shouldUseVirtualRoofEdges(mapType: string, productId: number, projectVersion: string, stateConfig?: any): boolean {
  if (isBlankMapInLite(mapType, stateConfig)) {
    return false;
  }
  return isBlankMap(mapType) && (isRMFamily(productId) || isEcoFoot2Plus(productId)) && (isASCE716or722Selected() || (shouldUseSetBackDistance(mapType, productId, projectVersion, stateConfig)));
}

export function shouldUpdateRoofEdgesOnPanelChange(mapType: string, productId: number, projectVersion: string, stateConfig?: any): boolean {
  return shouldUseVirtualRoofEdges(mapType, productId, projectVersion, stateConfig) && !shouldUseSetBackDistance(mapType, productId, projectVersion, stateConfig);
}

export function shouldUseSetBackDistance(mapType: string, productId: number, projectVersion: string, stateConfig?: any): boolean {
  if (isBlankMapInLite(mapType, stateConfig)) {
    return false;
  }
  return (isRMIFIProduct(productId) || applyEcoFoot2PlusRM10andEvoSetbackChanges(productId, projectVersion)) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId);
}

// need to update shouldUseDesiredBuildingLengthWidth function if product have setback but not desired building length and width
export function shouldUseDesiredBuildingLengthWidth(mapType: string, productId: number, projectVersion: string, stateConfig?: any): boolean{
  if (isBlankMapInLite(mapType, stateConfig)) {
    return false;
  }
  return isBlankMap(mapType) && shouldUseSetBackDistance(mapType, productId, projectVersion, stateConfig);
}
